import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IUploadService } from "@shared/interfaces/upload-service.interface";
import { PlanPlatform } from "@shared/types/plans/enum/plan-platform.enum";
import { PlanDto } from "@shared/types/plans/plan.dto";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import { Statuses } from "../Plans";

const requiredFieldRule = {
  required: true,
  whitespace: true,
  message: "This field is required",
};

interface IModal {
  open: boolean;
  plan: PlanDto | null;
  onClose: () => void;
  onSave: (data: PlanDto) => void;
  isLoading: boolean;
  isError?: boolean;
}

const ChangeplanModal = ({
  open,
  plan,
  onClose,
  onSave,
  isLoading,
  isError,
}: IModal) => {
  const isEditingMode = !!plan?.id;
  const [form] = Form.useForm();
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const uploadService = appInject<IUploadService>(DI_TOKENS.uploadService);

  useEffect(() => {
    if (plan) {
      form.setFieldsValue(plan);
    }
  }, [form, plan]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const isFieldDisabled = (field: keyof PlanDto) => {
    if (!isEditingMode) return false;
    return !["tokens", "status", "imageUrl", "description", "name"].includes(
      field,
    );
  };

  const handleFinish = (values: PlanDto) => {
    onSave(values);
  };

  return (
    <Modal
      title={isEditingMode ? "Change plan" : "Add plan"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form
        key={JSON.stringify(plan)}
        form={form}
        requiredMark={false}
        onFinish={handleFinish}
        layout="vertical"
      >
        {isError && (
          <Form.Item>
            <Alert
              message="Error"
              description="An error occurred while saving the plan data. Please try again."
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item name="name" label="Name" rules={[requiredFieldRule]}>
          <Input disabled={isFieldDisabled("name")} />
        </Form.Item>

        <Form.Item label="Image for Popup">
          <input
            type="file"
            accept="image/*"
            disabled={isFieldDisabled("imageUrl")}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setImageUploaded(false);
              setImageFile(file);
              if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
              }
              setImagePreview(file ? URL.createObjectURL(file) : null);
            }}
          />
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={imagePreview}
                alt="Selected"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />
              {imageUploaded === false && !isFieldDisabled("imageUrl") && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    disabled={imageUploading}
                    onClick={async () => {
                      if (!imageFile) return;
                      try {
                        setImageUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(imageFile);
                        if (url) {
                          setImageUploaded(true);
                        }
                        form.setFieldsValue({ imageUrl: url });
                      } catch (err) {
                        message.error("Image upload failed");
                      } finally {
                        setImageUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {imageUploading ? "Uploading..." : "Use Image"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Form.Item>

        <Form.Item name="imageUrl" label="Image" rules={[requiredFieldRule]}>
          <Input type="url" disabled={isFieldDisabled("imageUrl")} />
        </Form.Item>

        <Form.Item
          name="revenueCatAppId"
          label="RevenueCat App ID"
          rules={[requiredFieldRule]}
        >
          <Input disabled={isFieldDisabled("revenueCatAppId")} />
        </Form.Item>

        <Form.Item
          name="revenueCatProductId"
          label="RevenueCat Product ID"
          rules={[requiredFieldRule]}
        >
          <Input disabled={isFieldDisabled("revenueCatProductId")} />
        </Form.Item>

        <Form.Item
          name="reveuecatStoreIdentifier"
          label="Store Identifier"
          rules={[requiredFieldRule]}
        >
          <Input disabled={isFieldDisabled("reveuecatStoreIdentifier")} />
        </Form.Item>

        <Form.Item
          name="revenueCatPlatform"
          label="Platform"
          rules={[requiredFieldRule]}
        >
          <Select
            disabled={isFieldDisabled("revenueCatPlatform")}
            options={Object.values(PlanPlatform).map((platform) => ({
              label: platform,
              value: platform,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ type: "string" }]}
        >
          <Input.TextArea rows={3} disabled={isFieldDisabled("description")} />
        </Form.Item>

        <Form.Item
          name="tokens"
          label="Tokens"
          rules={[
            {
              required: true,
              message: "Tokens are required",
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null) {
                  return Promise.resolve();
                }
                if (!Number.isInteger(value)) {
                  return Promise.reject("Tokens must be an integer");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            disabled={isFieldDisabled("tokens")}
          />
        </Form.Item>

        <Form.Item name="status" label="Status" rules={[requiredFieldRule]}>
          <Select disabled={isFieldDisabled("status")} options={Statuses} />
        </Form.Item>

        <Space
          style={{ justifyContent: "flex-end", width: "100%", marginTop: 15 }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditingMode ? "Save" : "Add"}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChangeplanModal;
